/**
 * Gender Filter
 * Build gender filter expressions based on selectedGender and checkboxes
 */

import { state } from '../core/state.js';

// Track previous selectedGender to detect changes
let previousSelectedGender = null;

/**
 * Get selectedGender from state
 * Read from state.gender (which comes from URL after Search button click)
 * NOT from window.DetailsSelection (which reflects current form state)
 * @returns {string|null} - 'male' | 'female' | null
 */
export function getSelectedGender() {
  // Read from state.gender (which comes from URL after Search button click)
  // NOT from window.DetailsSelection (which reflects current form state)
  if (Array.isArray(state.gender) && state.gender.length > 0) {
    const gender = state.gender[0];
    if (gender === 'male' || gender === 'female') {
      return gender;
    }
  }
  return null;
}

/**
 * Reset gender filter checkboxes when selectedGender changes
 */
export function resetGenderFilterCheckboxesIfNeeded() {
  const currentSelectedGender = getSelectedGender();
  // If selectedGender changed, reset checkboxes and clear opposite gender
  if (previousSelectedGender !== currentSelectedGender) {
    state.filterCoed = false;
    // Clear opposite gender filter when selectedGender changes
    if (currentSelectedGender === 'male') {
      state.filterFemaleOnly = false;
    } else if (currentSelectedGender === 'female') {
      state.filterMaleOnly = false;
    }
    previousSelectedGender = currentSelectedGender;
  }
}

/**
 * Build Algolia gender filter expression
 * Combines selectedGender (from search form) and filter checkboxes
 * @returns {string|null}
 */
export function buildGenderFilterExpression() {
  // Get user's global gender selection from DetailsSelection
  const selectedGender = getSelectedGender();

  // Collect selected checkbox filters
  const selectedFilters = [];

  if (state.filterCoed) {
    selectedFilters.push('gender:"all"'); // coed
  }
  if (state.filterMaleOnly) {
    selectedFilters.push('gender:"male"'); // male only
  }
  if (state.filterFemaleOnly) {
    selectedFilters.push('gender:"female"'); // female only
  }

  // If no checkboxes selected and no selectedGender, return null (no filter)
  if (selectedFilters.length === 0 && !selectedGender) {
    return null;
  }

  // If checkboxes are selected, use them
  if (selectedFilters.length > 0) {
    // If selectedGender exists, filter checkboxes by it
    if (selectedGender) {
      // Filter: only include checkboxes that match selectedGender or coed
      const filtered = selectedFilters.filter((f) => {
        if (f === 'gender:"all"') return true; // Always include coed
        if (selectedGender === 'female') return f === 'gender:"female"';
        if (selectedGender === 'male') return f === 'gender:"male"';
        return false;
      });

      if (filtered.length === 0) {
        // If no matching filters, return base selection
        if (selectedGender === 'female') {
          return '(gender:"female" OR gender:"all")';
        } else if (selectedGender === 'male') {
          return '(gender:"male" OR gender:"all")';
        }
      }

      return filtered.length === 1
        ? filtered[0]
        : `(${filtered.join(' OR ')})`;
    }

    // No selectedGender, use all selected checkboxes
    return selectedFilters.length === 1
      ? selectedFilters[0]
      : `(${selectedFilters.join(' OR ')})`;
  }

  // No checkboxes selected, but selectedGender exists - use base selection
  if (selectedGender) {
    if (selectedGender === 'female') {
      return '(gender:"female" OR gender:"all")';
    } else if (selectedGender === 'male') {
      return '(gender:"male" OR gender:"all")';
    }
  }

  return null;
}

/**
 * Mark state as initialized for tracking
 */
export function markStateInitialized() {
  previousSelectedGender = getSelectedGender();
}

console.log('📦 Gender filter module loaded');
